/**
 * FuarlarSekmesi.jsx — Kullanıcı Kartı › Fuarlar (backlog #379; backend #378 `GET /users/{id}/kart`).
 *
 * Deniz'in istediği "hangi fuarlara atanmış ve giriş yapmış" üç parçaya ayrılır, çünkü giriş GEÇMİŞİ hiçbir sistemde tutulmadı
 * (karar 2026-10-13): hangi fuarda girdiği bilinmez, bilinen yalnızca üst çubukta ŞU AN seçili fuar ve son girişin zamanıdır.
 *   · Giriş            — seçili fuar + son giriş (bu sistemdeki)
 *   · Atandığı fuarlar — `user_to_fuar` (personelde elle atama; firma kullanıcısında firması fuara bağlanınca bir satır)
 *   · Firmasının çalıştığı fuarlar — firmasının herhangi bir rolde bağlı olduğu ya da canlı standı olan fuarlar (#187 kuralı);
 *     dekoratörlerin yalnızca standla geldiği fuarlar `user_to_fuar`a hiç girmez, bu yüzden ayrı listelenir.
 * Fuar özeti: { fuarId, fuarVar, fuarAdi, fuarKisaAdi, baslangic, bitis, silinmis, arsivde } (+ secili, firmasiCalisiyor / atanmis,
 * firmalar[{ firmaId, firmaAdi, roller, rolAdlari, standSayisi }]); yeniden eskiye gelir.
 */
import React from "react";
import { Badge, Card, CardBody, CardHeader, Table } from "reactstrap";
import { Building2, CalendarDays, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fuarAdi, tarihAraligi, tarihSaat } from "../../../../Components/Kullanici/KullaniciParcalari";

/** Bu temada `success` gri, `secondary` yeşil basılır: olumlu rozet `secondary`. */
function FuarDurumlari({ f }) {
  const { t } = useTranslation("yonetim");
  return (
    <span className="d-inline-flex align-items-center gap-1 flex-wrap">
      {f.secili && <Badge color="primary" className="fw-normal">{t("fuarlarSekmesi.secili")}</Badge>}
      {f.arsivde && <Badge color="light" className="text-body border fw-normal">{t("fuarlarSekmesi.arsivde")}</Badge>}
      {f.silinmis && <Badge color="danger" className="fw-normal">{t("fuarlarSekmesi.silinmis")}</Badge>}
    </span>
  );
}

function FuarHucresi({ f }) {
  const { t } = useTranslation("yonetim");
  return (
    <>
      <div className="fw-semibold text-break" title={f.fuarAdi || undefined}>
        {f.fuarVar === false ? t("fuarlarSekmesi.fuarKayitYok", { id: f.fuarId }) : fuarAdi(f)}
      </div>
      {tarihAraligi(f.baslangic, f.bitis) && <div className="small text-muted">{tarihAraligi(f.baslangic, f.bitis)}</div>}
    </>
  );
}

export default function FuarlarSekmesi({ kart }) {
  const { t } = useTranslation("yonetim");
  const u = kart.kullanici;
  const atandigi = kart.atandigiFuarlar || [];
  const firmaFuarlari = kart.firmaFuarlari || [];
  const secili = u.seciliFuar;

  return (
    <>
      <Card className="mb-3">
        <CardHeader className="bg-transparent d-flex align-items-center gap-2">
          <Clock size={16} className="text-muted" />
          <span className="fw-semibold">{t("fuarlarSekmesi.giris")}</span>
        </CardHeader>
        <CardBody>
          <div className="row g-3">
            <div className="col-md-6">
              <div className="small text-muted mb-1">{t("fuarlarSekmesi.sonGiris")}</div>
              {u.userLastLogin ? tarihSaat(u.userLastLogin) : <span className="text-muted">{t("fuarlarSekmesi.hicGirisYapmadi")}</span>}
            </div>
            <div className="col-md-6">
              <div className="small text-muted mb-1">{t("fuarlarSekmesi.seciliFuar")}</div>
              {secili ? (
                <>
                  <FuarHucresi f={secili} /> <FuarDurumlari f={{ ...secili, secili: false }} />
                </>
              ) : <span className="text-muted">{t("fuarlarSekmesi.fuarSeciliDegil")}</span>}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader className="bg-transparent d-flex align-items-center gap-2">
          <CalendarDays size={16} className="text-muted" />
          <span className="fw-semibold">{t("fuarlarSekmesi.atandigiFuarlar")}</span>
          <Badge color="light" className="text-body border fw-normal">{atandigi.length}</Badge>
        </CardHeader>
        <CardBody className={atandigi.length ? "p-0" : undefined}>
          {atandigi.length === 0 ? (
            <div className="text-muted small">{t("fuarlarSekmesi.atandigiFuarYok")}</div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light text-muted small">
                  <tr><th className="ps-3">{t("fuarlarSekmesi.fuar")}</th><th>{t("fuarlarSekmesi.durum")}</th><th>{t("fuarlarSekmesi.firmasi")}</th></tr>
                </thead>
                <tbody>
                  {atandigi.map((f) => (
                    <tr key={f.fuarId}>
                      <td className="ps-3"><FuarHucresi f={f} /></td>
                      <td><FuarDurumlari f={f} /></td>
                      <td className="small">
                        {f.firmasiCalisiyor ? t("fuarlarSekmesi.firmasiCalisiyor") : <span className="text-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mb-3">
        <CardHeader className="bg-transparent d-flex align-items-center gap-2">
          <Building2 size={16} className="text-muted" />
          <span className="fw-semibold">{t("fuarlarSekmesi.firmasininCalistigiFuarlar")}</span>
          <Badge color="light" className="text-body border fw-normal">{firmaFuarlari.length}</Badge>
        </CardHeader>
        <CardBody className={firmaFuarlari.length ? "p-0" : undefined}>
          {firmaFuarlari.length === 0 ? (
            <div className="text-muted small">
              {(u.firmalar || []).length === 0 ? t("fuarlarSekmesi.kullanicininFirmasiYok") : t("fuarlarSekmesi.firmasininCalistigiFuarYok")}
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light text-muted small">
                  <tr>
                    <th className="ps-3">{t("fuarlarSekmesi.fuar")}</th><th>{t("fuarlarSekmesi.durum")}</th>
                    <th>{t("fuarlarSekmesi.firma")}</th><th>{t("fuarlarSekmesi.atama")}</th>
                  </tr>
                </thead>
                <tbody>
                  {firmaFuarlari.map((f) => (
                    <tr key={f.fuarId}>
                      <td className="ps-3"><FuarHucresi f={f} /></td>
                      <td><FuarDurumlari f={f} /></td>
                      <td>
                        {(f.firmalar || []).map((fm) => (
                          <div key={fm.firmaId} className="small">
                            <span className="fw-semibold">{fm.firmaAdi || t("fuarlarSekmesi.firmaNo", { id: fm.firmaId })}</span>
                            {(fm.rolAdlari || []).length > 0 && <span className="text-muted"> · {fm.rolAdlari.join(", ")}</span>}
                            {fm.standSayisi > 0 && <span className="text-muted"> · {t("fuarlarSekmesi.standSayisi", { count: fm.standSayisi })}</span>}
                          </div>
                        ))}
                      </td>
                      <td>
                        {f.atanmis
                          ? <Badge color="secondary" className="fw-normal">{t("fuarlarSekmesi.atanmis")}</Badge>
                          : <Badge color="light" className="text-body border fw-normal">{t("fuarlarSekmesi.atanmamis")}</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
